import React, { useEffect, useState } from "react";
import SideMenu from "../../../sharedScreens/menuItems/SideMenu";
import MuiButton from "../../../components/basicComponent/MuiButton";
import MuiSearchSelect from "../../../components/basicComponent/MuiSearchSelect";
import Header from "../../../sharedScreens/Header";
import UserListFilter from "../../../components/composite/UserListFilter";
import { MuiInput } from "../../../components/basicComponent/MuiInput";
import { useFormik } from "formik";
import { CorporatePostpaidAccountSchema } from "../../../validations/Schema";
import { MuiDropDown } from "../../../components/basicComponent/MuiDropDown";
import { UtilService } from "../../../services/shared/UtilService";
import { msgTypes } from "../../../constants/msgTypes";
import { useSweetAlert } from "../../../services/customHooks/useSweetAlert";

interface FormValues {
  corporateName: string;
  creditLimit: string;
}

const ITEMS_PER_PAGE = 5;
const CorporatePostpaidAccount = () => {
  const [companyList, setCompanyList] = useState<[]>([]);
  const { showSuccess, showError, showInfo, showConfirm } = useSweetAlert();
  const [postpaidAccountList, setPostpaidAccountList] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [list, setList] = useState([]);

  const initialValues: FormValues = {
    corporateName: "",
    creditLimit: "",
  };

  const formik = useFormik<FormValues>({
    initialValues: initialValues,
    validationSchema: CorporatePostpaidAccountSchema,
    enableReinitialize: true,

    onSubmit: async (values, { resetForm }) => {
      try {
        const requestBody = {
          user: Number(values.corporateName), // assuming this is corporate/user ID
          credit_limit: Number(values.creditLimit),
          used_credit: 0,
        };

        const response = await UtilService.CorporatePostpaidAccount(
          requestBody
        );

        if (response?.statusMsg === msgTypes.SUCCESS.msg) {
          showSuccess(
            "Success!",
            "Postpaid account has been created successfully.",
            {
              timer: 3000,
              timerProgressBar: true,
            }
          );
          resetForm();
        } else {
          showError(
            "Oops...Something went wrong while adding postapid account!"
          );
        }
      } catch (error) {
        console.error("API Error:", error);
      }
    },
  });

  useEffect(() => {
    getCompanyList();
    fetchPostpaidAccountList(); // call the new GET API
  }, []);

  const getCompanyList = async (filterPayload?: {}) => {
    const response = await UtilService.getBusinessName();
    if (response.statusMsg === msgTypes.SUCCESS.msg) {
      const arr = response?.data?.map(
        (i: {
          businessName: any;
          id: any;
          user?: {
            id: number;
          };
        }) => ({
          id: i?.id,
          label: i?.businessName,
          value: i?.id,
          userId: i.user?.id ?? null, // safe optional chaining
        })
      );
      setCompanyList(arr);
    }
  };

  const getPaginatedData = (data: any[], page: number, perPage: number) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return data.slice(startIndex, endIndex);
  };

  const fetchPostpaidAccountList = async () => {
    try {
      const response = await UtilService.getPostpaidAccountList({});

      if (response.statusMsg === msgTypes.SUCCESS.msg) {
        setPostpaidAccountList(response.data || []);
        const fullList = response.data || [];
        setList(fullList);
        setPostpaidAccountList(
          getPaginatedData(fullList, currentPage, itemsPerPage)
        );
      } else {
        showError("Failed to fetch postpaid account list");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setPostpaidAccountList(getPaginatedData(list, newPage, itemsPerPage)); // ✅ update view
  };

  return (
    <>
      <div className="grow">
        <SideMenu />
        <div className="wrapper flex grow flex-col">
          <Header />
          <main className="grow content" id="content" role="content">
            <div className="container-fixed">
              <div className="card-header text-center">
                <h3 className="card-title">Corporate Postpaid Account</h3>
              </div>
              <div className="card card-custom">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row mt-4">
                        <div className="col-md-4 mt-3">
                          <MuiSearchSelect
                            label="Corporate Name"
                            placeholder="Corporate Name"
                            value={formik.values.corporateName}
                            isRequired={true}
                            onChange={(value, id) =>
                              formik.setFieldValue("corporateName", value)
                            }
                            options={companyList}
                            isInvalid={
                              formik.touched.corporateName &&
                              Boolean(formik.errors.corporateName)
                            }
                            errorText={
                              formik.touched.corporateName &&
                              formik.errors.corporateName
                                ? formik.errors.corporateName
                                : ""
                            }
                          />
                        </div>

                        <div className="col-md-4 mt-3">
                          <MuiInput
                            label="Credit Limit"
                            placeholder="Credit Limit"
                            isRequired={true}
                            name="description"
                            value={formik.values.creditLimit}
                            onChange={(e: any) =>
                              formik.setFieldValue("creditLimit", e)
                            }
                            onBlur={formik.handleBlur}
                            error={
                              formik.touched.creditLimit &&
                              !!formik.errors.creditLimit
                            }
                            helperText={
                              formik.touched.creditLimit &&
                              formik.errors.creditLimit
                            }
                          />
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                            marginTop: "8px",
                          }}
                        >
                          <div className="user-button w-full mt-4">
                            <MuiButton
                              className="clear-btn "
                              label="Clear"
                              onClick={() => formik.resetForm()}
                            />
                            <MuiButton
                              className="main-btn get-btn"
                              variant="contained"
                              size="large"
                              type="submit"
                              label={"Submit"}
                              onClick={() => formik.handleSubmit()}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead className="table-dark">
                          <tr>
                            <th>user</th>
                            <th>credit Limit</th>
                            <th>used Credit</th>
                            <th>Last Limit Updated</th>
                            <th>Limit Updated By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {postpaidAccountList.length > 0 ? (
                            postpaidAccountList.map((item, index) => (
                              <tr key={index}>
                                <td>
                                  <div className="sort">
                                    {item.userName || "-"}
                                  </div>
                                </td>
                                <td>
                                  <div className="sort">
                                    {item.creditLimit ?? "-"}
                                  </div>
                                </td>
                                <td>
                                  <div className="sort">
                                    {item.usedCredit ?? "-"}
                                  </div>
                                </td>
                                <td>
                                  <div className="sort">
                                    {item.lastLimitUpdated
                                      ? new Date(
                                          item.lastLimitUpdated
                                        ).toLocaleString()
                                      : "-"}
                                  </div>
                                </td>
                                <td>
                                  <div className="sort">
                                    {item.limitUpdatedByName || "-"}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center">
                                <div className="d-flex flex-column align-items-center justify-content-center py-4">
                                  <div
                                    className="d-flex align-items-center justify-content-center bg-light rounded-circle mb-3"
                                    style={{ width: "80px", height: "80px" }}
                                  >
                                    <img
                                      src="media/images/remove-folder.png"
                                      alt="remove-folder"
                                      style={{
                                        maxWidth: "40px",
                                        maxHeight: "40px",
                                      }}
                                    />
                                  </div>
                                  <h5 className="text-muted mb-0 fw-bold fs-5">
                                    No data available
                                  </h5>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-5 text-gray-600 text-2sm font-medium">
                      <div className="pagination">
                        <button
                          className="btn "
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="ki-outline ki-black-left rtl:transform rtl:rotate-180"></i>
                        </button>
                        <button className="btn active disabled">
                          {currentPage}
                        </button>
                        <button
                          className="btn "
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage * itemsPerPage >= list.length}
                        >
                          <i className="ki-outline ki-black-right rtl:transform rtl:rotate-180"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default CorporatePostpaidAccount;
